import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import { Link as RouterLink } from 'react-router-dom';
import { SupportTicketPayload } from '../types';
import {
  submitSupportTicket,
  fetchSupportTickets,
  SupportTicket,
} from '../services/driverWorkspaceService';

const categories: Array<{ value: SupportTicketPayload['category']; label: string }> = [
  { value: 'invalid_contact', label: 'Invalid contact' },
  { value: 'payment', label: 'Payment issue' },
  { value: 'ride_quality', label: 'Lead quality' },
  { value: 'account', label: 'Account' },
  { value: 'technical', label: 'Technical issue' },
  { value: 'other', label: 'Other' },
];

const statusColors: Record<string, 'default' | 'info' | 'success' | 'warning'> = {
  open: 'info',
  in_review: 'warning',
  resolved: 'success',
  closed: 'default',
};

const statusLabels: Record<string, string> = {
  open: 'Open',
  in_review: 'In review',
  resolved: 'Resolved',
  closed: 'Closed',
};

const Support: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'new' | 'tickets'>('new');
  const [payload, setPayload] = useState<SupportTicketPayload>({
    category: 'invalid_contact',
    subject: '',
    message: '',
  });
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState<string | null>(null);
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);

  useEffect(() => {
    if (activeTab === 'tickets') {
      setTicketsLoading(true);
      fetchSupportTickets()
        .then(setTickets)
        .catch(() => setSnack('Unable to load tickets.'))
        .finally(() => setTicketsLoading(false));
    }
  }, [activeTab]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    try {
      await submitSupportTicket(payload);
      setPayload({ category: 'invalid_contact', subject: '', message: '' });
      setSnack('Support request submitted. Switch to "My Tickets" to track status.');
    } catch (error: any) {
      setSnack(error?.response?.data?.message || 'Unable to submit support request.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack spacing={3} sx={{ maxWidth: 1040, mx: 'auto' }}>
      <Paper
        elevation={0}
        sx={{ p: { xs: 2.5, md: 3 }, bgcolor: '#061b31', color: 'white', background: 'linear-gradient(135deg,#061b31,#0b3156)' }}
      >
        <Typography variant="h4" fontWeight={900}>Support center</Typography>
        <Typography color="rgba(255,255,255,0.72)" mt={0.75}>
          Report invalid contacts, payment issues, or lead quality problems from one place.
        </Typography>
      </Paper>

      <Paper variant="outlined" sx={{ p: 1 }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} variant="scrollable" scrollButtons="auto">
          <Tab label="New request" value="new" />
          <Tab label="My tickets" value="tickets" />
        </Tabs>
      </Paper>

      {activeTab === 'new' && (
        <Grid container spacing={2.5}>
          <Grid item xs={12} md={7}>
            <Paper component="form" onSubmit={submit} variant="outlined" sx={{ p: 2.5 }}>
              <Typography variant="h6" fontWeight={900}>Create support request</Typography>
              <Stack spacing={2} mt={2}>
                <TextField
                  select
                  label="Category"
                  value={payload.category}
                  onChange={(event) => setPayload((prev) => ({ ...prev, category: event.target.value as SupportTicketPayload['category'] }))}
                >
                  {categories.map((category) => (
                    <MenuItem key={category.value} value={category.value}>{category.label}</MenuItem>
                  ))}
                </TextField>
                <TextField
                  label="Subject"
                  value={payload.subject}
                  onChange={(event) => setPayload((prev) => ({ ...prev, subject: event.target.value }))}
                  required
                />
                <TextField
                  label="Message"
                  minRows={5}
                  multiline
                  value={payload.message}
                  onChange={(event) => setPayload((prev) => ({ ...prev, message: event.target.value }))}
                  required
                  helperText="Include ride route, phone number issue, payment order, or screenshot details when relevant."
                />
                <Box>
                  <Button type="submit" variant="contained" color="secondary" disabled={saving}>
                    {saving ? 'Submitting...' : 'Submit request'}
                  </Button>
                </Box>
              </Stack>
            </Paper>
          </Grid>
          <Grid item xs={12} md={5}>
            <Stack spacing={2}>
              <Alert severity="info">
                Invalid contact reports can be reviewed by admin and credited back when confirmed.
              </Alert>
              <Paper variant="outlined" sx={{ p: 2.5 }}>
                <Typography variant="h6" fontWeight={900}>Quick actions</Typography>
                <Stack spacing={1.25} mt={2}>
                  <Button component={RouterLink} to="/activity" variant="outlined">Report from unlock history</Button>
                  <Button component={RouterLink} to="/wallet" variant="outlined">Payment history and invoices</Button>
                  <Button component={RouterLink} to="/settings?tab=notifications" variant="outlined">Notification settings</Button>
                </Stack>
              </Paper>
              <Paper variant="outlined" sx={{ p: 2.5 }}>
                <Typography variant="h6" fontWeight={900}>FAQ shortcuts</Typography>
                <Typography variant="body2" color="text.secondary" mt={1}>
                  Contacts and prices stay locked until unlock. Broad push alerts may include rides outside your saved routes; the ride workspace ranks the best matches after opening.
                </Typography>
              </Paper>
            </Stack>
          </Grid>
        </Grid>
      )}

      {activeTab === 'tickets' && (
        <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
          {ticketsLoading ? (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress />
            </Box>
          ) : tickets.length === 0 ? (
            <Box p={5} textAlign="center">
              <SupportAgentIcon sx={{ fontSize: 52, color: 'rgba(7,24,45,0.18)', mb: 1.5 }} />
              <Typography variant="h6" fontWeight={700} mb={0.75}>No tickets yet</Typography>
              <Typography color="text.secondary" mb={2.5}>
                Submit a support request and it will appear here with its status.
              </Typography>
              <Button variant="outlined" onClick={() => setActiveTab('new')}>Create a request</Button>
            </Box>
          ) : (
            <Stack divider={<Divider />}>
              {tickets.map((ticket) => (
                <Box key={ticket.id} p={2.25}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2} flexWrap="wrap">
                    <Box flex={1} minWidth={0}>
                      <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                        <Chip
                          label={statusLabels[ticket.status] || ticket.status}
                          color={statusColors[ticket.status] || 'default'}
                          size="small"
                        />
                        <Chip
                          label={categories.find((c) => c.value === ticket.category)?.label || ticket.category}
                          size="small"
                          variant="outlined"
                        />
                      </Stack>
                      <Typography fontWeight={700}>{ticket.subject}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Submitted {new Date(ticket.createdAt).toLocaleDateString()} · Updated {new Date(ticket.updatedAt).toLocaleDateString()}
                      </Typography>
                      {ticket.adminResponse && (
                        <Box mt={1.5} p={1.5} sx={{ bgcolor: '#f0f7ff', borderRadius: 1, borderLeft: '3px solid #1976d2' }}>
                          <Typography variant="caption" color="primary" fontWeight={700}>Support reply</Typography>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.25 }}>{ticket.adminResponse}</Typography>
                          {ticket.respondedAt && (
                            <Typography variant="caption" color="text.secondary">{new Date(ticket.respondedAt).toLocaleDateString()}</Typography>
                          )}
                        </Box>
                      )}
                    </Box>
                    {ticket.priority === 'high' && (
                      <Chip label="High priority" color="warning" size="small" />
                    )}
                  </Box>
                </Box>
              ))}
            </Stack>
          )}
        </Paper>
      )}

      <Snackbar open={Boolean(snack)} autoHideDuration={4000} onClose={() => setSnack(null)} message={snack} />
    </Stack>
  );
};

export default Support;
